import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
	Box,
	CircularProgress,
	Container,
	ListItemIcon,
	ListItemText,
	Menu,
	MenuItem,
	Typography,
	useMediaQuery,
} from '@mui/material';
import {Entry, EntityType, PropertyDefinition, PropertyDefinitionBelongsTo, PropertyDefinitionHas, RepositoryByIdResponse, RepositoryEntry, Theme, PressedKey} from '../types';
import EntriesTable from '../components/EntriesTable';
import EntriesList, { EntriesListItem } from '../components/EntriesList';
import {AddRepositoryEntryFormDialog, useRepositoryEntryDialog} from '../components/AddRepositoryEntryDialog';
import {useAppDispatch, useAppSelector} from '../hooks';
import {useContentNavigationEffects} from '../hooks/useContentNavigationEffects';
import {useEntriesFilterState} from '../hooks/useEntriesFilterState';
import {useKeyboardEffects} from '../components/KeyboardEffects';
import ConfirmDeleteEntryDialog, {useConfirmDeleteEntryDialog} from '../components/ConfirmDeleteEntryDialog';
import { RowMenuItem } from '../components/EntryMenu';
import { RepositoryEntriesRowDto, useGetRepositoryByIdQuery, useGetRepositoryEntriesByFilterQuery, useRepositoryEntryArchiveMutation, useRepositoryEntryUnarchiveMutation, useRepositoryEntryUpdateMutation } from '../services/repositoryEntries';
import { selectIsAuthenticated } from './authSlice';
import { entriesUpdateMany, repositoryEntryArchiveLocalAction, repositoryEntryUnarchiveLocalAction, repositoryEntryUpdateLocalAction, selectAllPropertyDefinitions, selectAllRepositoryEntries, setActiveRepositoryId, setRepositoryHeaderTitle, selectAllEntryProperties, selectAllRepositories, selectIsEntriesHydrated } from './contentSlice';
import { selectIsAnyDialogOpen } from './formDataSlice';
import { navigateRollOutward } from './navigationSlice';
import { repositoryEntryRankUpdatesForReorder } from '../common/fractionalRank';
import { entryPropertyGetReadableValue, getRepositorySingleUnitLabel, isEntryTimestampPropertyDefinition, populateRepositoryEntriesForTable, repositoryEntriesApiRowToPopulatedEntry, sortEntryByRank } from '../common/util';
import { matchesResolvedDataFilter } from '../common/offlineDataFilter';
import { Link, useNavigate, useParams } from 'react-router-dom';
import useAuth from '../components/Auth';
import FabAreaWrapperBottomRight from '../components/FabAreaWrapperBottomRight';
import FabAreaWrapperBottomCenter from '../components/FabAreaWrapperBottomCenter';
import SettingsIcon from '@mui/icons-material/Settings';
import { Helmet } from 'react-helmet';
import config from '../common/config';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import EditRepositoryEntryFieldDialog from '../components/EditRepositoryEntryFieldDialog';
import EntriesFilterDialog from '../components/EntriesFilterDialog';
import RepositoryEntriesFilterArea from '../components/RepositoryEntriesFilterArea';
import {
	localStorageGetEntriesTableVisiblePropertyIds,
	localStorageSetEntriesTableVisiblePropertyIds,
} from '../localStorage';
import { ActionAreaBtn, ActionAreaFab } from '../components/ActionAreaFab';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ShareIcon from '@mui/icons-material/Share';
import ShareRepositoryDialog from '../components/ShareRepositoryDialog';
import { RepositoryAccessKeyType } from '../services/repositoryAccessKeys';
import { useSharedView } from '../hooks/useSharedView';
import { isNoAccessQueryError, isNotFoundQueryError } from '../common/queryErrors';
import NotFoundView from './NotFoundView';
import NoAccessView from './NoAccessView';
import { useGetSharedCollectionQuery } from '../services/sharedCollections';

const RepositoryEntriesView = () => {
	const {
		focusId,
		selectedEntryId,
		handleEntryClick,
		handleEntryFocus,
		handleEntryBlur,
	} = useContentNavigationEffects();
	const dispatch = useAppDispatch();
	const setKeyboardMappings = useKeyboardEffects();
	const {shareKey, isSharedView, toPath} = useSharedView();

	const isAnyDialogOpen = useAppSelector(selectIsAnyDialogOpen);
	const [fieldEditTarget, setFieldEditTarget] = useState<{ entry: RepositoryEntriesRowDto; def: PropertyDefinition } | null>(null);
	const [pendingForeignCreateLink, setPendingForeignCreateLink] = useState<{
		entryId: string;
		def: PropertyDefinitionBelongsTo | PropertyDefinitionHas;
		currentEntryIds: string[];
	} | null>(null);
	const {isAddRepositoryEntryDialogOpen, addRepositoryEntryDialog, openAddRepositoryEntryDialog, closeAddRepositoryEntryDialog} = useRepositoryEntryDialog();
	const anyBlockingDialog = isAnyDialogOpen || Boolean(fieldEditTarget);

	const handleKeyPressEscape = useCallback(() => {
		if (isSharedView || anyBlockingDialog) {
			return;
		}
		dispatch(navigateRollOutward());
	}, [dispatch, isSharedView, anyBlockingDialog]);

	const { id: repositoryIdParam } = useParams();
	const repositoryId = repositoryIdParam ?? null;
	const [visiblePropertyIds, setVisiblePropertyIds] = useState<string[] | null>(null);
	const allRepositoryEntries = useAppSelector(selectAllRepositoryEntries);
	const allRepositories = useAppSelector(selectAllRepositories);
	const allPropertyDefinitions = useAppSelector(selectAllPropertyDefinitions);
	const allEntryProperties = useAppSelector(selectAllEntryProperties);
	const isAuthenticated = useAppSelector(selectIsAuthenticated);
	const isCloudData = isAuthenticated || isSharedView;
	/** A shared view loads everything through the share link's endpoint instead of the owner's routes. */
	const isOwnerCloudData = isAuthenticated && !isSharedView;
	/** Kept from the latest shared collection response, which is requested with the filters that depend on it. */
	const [sharedRepositoryResponse, setSharedRepositoryResponse] = useState<RepositoryByIdResponse | null>(null);
	const isEntriesHydrated = useAppSelector(selectIsEntriesHydrated);
	const isSmScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
	const navigate = useNavigate();

	const {isAuthenticating} = useAuth();
	const { data: ownerRepositoryResponse, error: repositoryByIdError, isLoading: isRepositoryByIdLoading } = useGetRepositoryByIdQuery(repositoryId as string, {
		skip: !isOwnerCloudData || isAuthenticating || !repositoryId,
	});
	const repositoryByIdResponse = isSharedView ? sharedRepositoryResponse : ownerRepositoryResponse;
	const isDebugMcpMode = process.env.REACT_APP_DEBUG_MCP_MODE === 'true';
	if (isDebugMcpMode) {
		console.warn('DEBUG MCP MODE is enabled. AI tool data will be fetched.');
	}
	const repository = useMemo(() => {
		if (isCloudData) {
			return repositoryByIdResponse?.repository;
		}
		return allRepositories.find((entry) => entry.id === repositoryId) ?? null;
	}, [repositoryByIdResponse?.repository, isCloudData, allRepositories, repositoryId]);
	const repositoryPropertyDefinitions = useMemo(() => {
		const raw = isCloudData
			? repositoryByIdResponse?.propertyDefinitions ?? []
			: allPropertyDefinitions.filter((entry) => entry.parentId === repositoryId);
		return [...raw].sort(sortEntryByRank);
	}, [repositoryByIdResponse?.propertyDefinitions, allPropertyDefinitions, isCloudData, repositoryId]);

	const {
		whereFilter,
		includeArchived,
		setIncludeArchived,
		resolvedDataFilter,
		isEntriesFilterDialogOpen,
		openFilters,
		closeFilters,
		chartFilterPropertyOptions,
		handleRemoveWhereFilterChip,
		applyWhereAndArchived,
	} = useEntriesFilterState(repositoryPropertyDefinitions);

	const { data: ownerEntriesResponse, isLoading: isOwnerEntriesLoading } = useGetRepositoryEntriesByFilterQuery({
		repositoryId: repositoryId as string,
		dataFilter: resolvedDataFilter,
		includeArchived,
		sortDir: 'asc',
	}, {
		skip: !isOwnerCloudData || isAuthenticating || !repositoryId,
	});
	const { data: sharedCollection, isLoading: isSharedCollectionLoading, error: sharedCollectionError } = useGetSharedCollectionQuery({
		shareKey: shareKey ?? '',
		dataFilter: resolvedDataFilter,
		includeArchived,
		sortDir: 'asc',
	}, {
		skip: !isSharedView,
	});
	useEffect(() => {
		if (sharedCollection) {
			setSharedRepositoryResponse({ repository: sharedCollection.repository, propertyDefinitions: sharedCollection.propertyDefinitions });
		}
	}, [sharedCollection]);
	const repositoryEntriesResponse = isSharedView ? sharedCollection?.entries : ownerEntriesResponse;
	const isRepositoryEntriesLoading = isSharedView ? isSharedCollectionLoading : isOwnerEntriesLoading;
	const [collectionMenuAnchorEl, setCollectionMenuAnchorEl] = useState<HTMLElement | null>(null);
	const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
	const entriesTableFieldOptions = useMemo(
		() =>
			repositoryPropertyDefinitions.map((p) => ({
				id: p.id as string,
				label: p.label?.trim() ? p.label : p.slug,
			})),
		[repositoryPropertyDefinitions],
	);
	const tablePropertyDefinitions = useMemo(() => {
		if (
			isSmScreen
			|| !repositoryPropertyDefinitions.length
			|| visiblePropertyIds == null
		) {
			return repositoryPropertyDefinitions;
		}
		const byId = new Map(repositoryPropertyDefinitions.map((d) => [d.id as string, d]));
		const ordered = visiblePropertyIds
			.map((id) => byId.get(id))
			.filter((d): d is PropertyDefinition => Boolean(d));
		return ordered.length ? ordered : repositoryPropertyDefinitions;
	}, [isSmScreen, repositoryPropertyDefinitions, visiblePropertyIds]);

	useEffect(() => {
		if (!repositoryId) {
			return;
		}
		setVisiblePropertyIds(localStorageGetEntriesTableVisiblePropertyIds(repositoryId));
	}, [repositoryId]);

	useEffect(() => {
		if (!repositoryPropertyDefinitions.length || !repositoryId) {
			return;
		}
		setVisiblePropertyIds((prev) => {
			if (prev == null) {
				return null;
			}
			const valid = new Set(repositoryPropertyDefinitions.map((d) => String(d.id)));
			const filtered = prev.filter((id) => valid.has(id));
			if (filtered.length === 0 || filtered.length === repositoryPropertyDefinitions.length) {
				localStorageSetEntriesTableVisiblePropertyIds(repositoryId, null);
				return null;
			}
			if (filtered.length !== prev.length) {
				localStorageSetEntriesTableVisiblePropertyIds(repositoryId, filtered);
			}
			return filtered;
		});
	}, [repositoryPropertyDefinitions, repositoryId]);
	const populatedForTable = useMemo((): RepositoryEntriesRowDto[] => {
		if (isCloudData) {
			const base = (repositoryEntriesResponse?.rows ?? []) as RepositoryEntriesRowDto[];
			return base.map(
				(row) => repositoryEntriesApiRowToPopulatedEntry(row) as RepositoryEntriesRowDto,
			);
		}
		if (!repository) {
			return [];
		}
		return populateRepositoryEntriesForTable(repository, allRepositoryEntries, allPropertyDefinitions, allEntryProperties)
			.filter((row) => matchesResolvedDataFilter(row, resolvedDataFilter))
			.sort(sortEntryByRank);
	}, [isCloudData, repositoryEntriesResponse?.rows, repository, allRepositoryEntries, allPropertyDefinitions, allEntryProperties, resolvedDataFilter]);
	const nonArchivedEntries = useMemo(
		() => populatedForTable.filter((entry) => !entry.isArchived),
		[populatedForTable],
	);
	const archivedEntries = useMemo(
		() => populatedForTable.filter((entry) => entry.isArchived).reverse(),
		[populatedForTable],
	);
	const tableEntries = useMemo(() => includeArchived
		? [...nonArchivedEntries, ...archivedEntries]
		: nonArchivedEntries,
	[includeArchived, nonArchivedEntries, archivedEntries]);
	const {isConfirmDeleteDialogOpen, confirmDeleteDialogEntry, openConfirmDeleteDialog, closeConfirmDeleteDialog, submitConfirmDeleteDialog} = useConfirmDeleteEntryDialog();
	const [entryArchiveMutation] = useRepositoryEntryArchiveMutation();
	const [entryUnarchiveMutation] = useRepositoryEntryUnarchiveMutation();
	const [repositoryEntryUpdateMutation] = useRepositoryEntryUpdateMutation();
	const rankByEntryIdRef = useRef<Map<string, string | number>>(new Map());

	const handleCellEdit = useCallback((row: RepositoryEntriesRowDto, def: PropertyDefinition) => {
		setFieldEditTarget({entry: row, def});
	}, []);

	const closeFieldEditDialog = useCallback(() => setFieldEditTarget(null), []);

	const handleFieldEditSubmitValues = useCallback(
		(values: Record<string, unknown>) => {
			if (!fieldEditTarget) {
				return;
			}
			const id = fieldEditTarget.entry.id;
			if (isAuthenticated) {
				repositoryEntryUpdateMutation({id, values, repositoryId: repository?.id});
			} else {
				dispatch(repositoryEntryUpdateLocalAction({id, values}));
			}
		},
		[fieldEditTarget, isAuthenticated, repositoryEntryUpdateMutation, dispatch]
	);

	const handleAddDialogCreated = useCallback((createdEntry: RepositoryEntry) => {
		if (!pendingForeignCreateLink) {
			return;
		}
		const {entryId, def, currentEntryIds} = pendingForeignCreateLink;
		const uniqueIds = Array.from(new Set([...currentEntryIds, createdEntry.id]));
		const nextValues =
			def.type === EntityType.PropertyDefinitionBelongsTo && !def.allowMany
				? [createdEntry.id]
				: uniqueIds;
		if (isAuthenticated) {
			repositoryEntryUpdateMutation({id: entryId, values: {[def.slug]: nextValues}});
		} else {
			dispatch(repositoryEntryUpdateLocalAction({id: entryId, values: {[def.slug]: nextValues}}));
		}
		setPendingForeignCreateLink(null);
	}, [pendingForeignCreateLink, isAuthenticated, repositoryEntryUpdateMutation, dispatch]);

	useEffect(() => {
		if (!isSharedView && !isRepositoryByIdLoading && repository && repositoryByIdResponse?.propertyDefinitions.length === 0) {
			navigate(`/collection/${repository?.id}/settings`);
		}
	}, [isSharedView, isRepositoryByIdLoading, repositoryByIdResponse?.propertyDefinitions.length, navigate, repository]);

	const handleKeyPressSpace = useCallback(() => {
		if (!isSharedView && !anyBlockingDialog) {
			openAddRepositoryEntryDialog({repositoryId: repository?.id});
		}
	}, [isSharedView, anyBlockingDialog, openAddRepositoryEntryDialog, repository?.id]);

	const handleKeyPressEnter = useCallback((event?: KeyboardEvent) => {
		if (!isSmScreen || anyBlockingDialog) {
			return;
		}
		if (selectedEntryId) {
			navigate(toPath(`/entries/${selectedEntryId}`));
			return;
		}

		if (focusId) {
			event?.preventDefault();
			event?.stopPropagation();
			handleEntryClick(focusId);
		}
	}, [isSmScreen, anyBlockingDialog, selectedEntryId, navigate, toPath, focusId, handleEntryClick]);

	const handleKeyPressBackspace = useCallback(() => {
		if (isSharedView || anyBlockingDialog) {
			return;
		}
		const row = tableEntries.find((entry) => entry.id === focusId);
		if (row && repository) {
			openConfirmDeleteDialog({ ...row, type: EntityType.RepositoryEntry, repositoryId: repository?.id } as { id: string; type: EntityType.RepositoryEntry; repositoryId: string } as Entry);
		}
	}, [isSharedView, focusId, tableEntries, openConfirmDeleteDialog, anyBlockingDialog, repository?.id]);

	useEffect(() => {
		setKeyboardMappings({
			[PressedKey.Escape]: handleKeyPressEscape,
			[PressedKey.Enter]: handleKeyPressEnter,
			[PressedKey.Space]: handleKeyPressSpace,
			[PressedKey.Backspace]: handleKeyPressBackspace,
		});
		return () => {
			setKeyboardMappings(null);
		};
	}, [handleKeyPressEscape, handleKeyPressEnter, handleKeyPressSpace, handleKeyPressBackspace, setKeyboardMappings]);

	const selectedListEntry = isSmScreen ? tableEntries.find((entry) => entry.id === selectedEntryId) : undefined;

	const [isListReordering, setIsListReordering] = useState(false);
	const timelineDateProperties = useMemo(
		() => repositoryPropertyDefinitions.filter((def) => def.type === EntityType.PropertyDefinitionDate && !isEntryTimestampPropertyDefinition(def)),
		[repositoryPropertyDefinitions],
	);
	const handleViewAsTimeline = (def: PropertyDefinition) => navigate(toPath(`/collection/${repository?.id}/timeline?property=${encodeURIComponent(def.slug)}`));
	const [menuAnchorEl, setMenuAnchorEl] = useState<Element | null>(null);
	const [menuEntry, setMenuEntry] = useState<null | Entry>(null);

	const handleRowMenuBtnClick = (item: RowMenuItem, el?: Element) => {
		setMenuEntry(item as Entry);
		if (el) {
			setMenuAnchorEl(el);
		}
	};

	const closeTableRowMenu = () => setMenuEntry(null);

	const handleTableRowMenuDelete = useCallback((id: string) => {
		closeTableRowMenu();
		const row = tableEntries.find((entry) => entry.id === id);
		if (row && repository) {
			openConfirmDeleteDialog({ ...row, type: EntityType.RepositoryEntry, repositoryId: repository?.id } as { id: string; type: EntityType.RepositoryEntry; repositoryId: string } as Entry);
		}
	}, [tableEntries, openConfirmDeleteDialog, repository?.id]);

	const handleTableRowMenuArchive = (id: string) => {
		if (isAuthenticated) {
			entryArchiveMutation({ entryId: id, repositoryId: repository?.id });
		} else {
			dispatch(repositoryEntryArchiveLocalAction(id));
		}
		closeTableRowMenu();
	};

	const handleTableRowMenuUnarchive = (id: string) => {
		if (isAuthenticated) {
			entryUnarchiveMutation({ entryId: id, repositoryId: repository?.id });
		} else {
			dispatch(repositoryEntryUnarchiveLocalAction(id));
		}
		closeTableRowMenu();
	};

	const handleTableRowMenuGoTo = (id: string) => {
		closeTableRowMenu();
		navigate(toPath(`/entries/${id}`));
		// navigateToLocationId(id);
	};


	// /** Keep header title in sync with current repository (parallel render tree). */
	useEffect(() => {
		if (repository?.title) {
			dispatch(setRepositoryHeaderTitle(repository.title));
		}
		if (repository?.id) {
			dispatch(setActiveRepositoryId(repository.id));
		}
		return () => {
			dispatch(setRepositoryHeaderTitle(null));
		};
	}, [dispatch, repository?.title, repository?.id]);


	const confirmDeleteDialogTitle = useMemo(() => confirmDeleteDialogEntry && repository ? `Delete ${getRepositorySingleUnitLabel(repository).toLowerCase()}?` : '', [confirmDeleteDialogEntry, repository]);
	const confirmDeleteEntryLabel = useMemo(() => {
		if (!confirmDeleteDialogEntry) {
			return '';
		}
		return populatedForTable.find((e) => e.id === confirmDeleteDialogEntry.id)?.label ?? '';
	}, [confirmDeleteDialogEntry, populatedForTable]);

	const handleReorder = useCallback((orderedIds: string[]) => {
		const currentRepositoryId = repository?.id;
		if (!currentRepositoryId) {
			return;
		}
		const rankById = rankByEntryIdRef.current.size
			? rankByEntryIdRef.current
			: new Map(tableEntries.map((e) => [e.id, e.rank]));
		const rankUpdates = repositoryEntryRankUpdatesForReorder(orderedIds, rankById);
		const updates = rankUpdates.map((update) => ({
			...update,
			type: EntityType.RepositoryEntry,
			repositoryId: currentRepositoryId,
		}));
		if (!updates.length) {
			return;
		}
		for (const update of updates) {
			rankByEntryIdRef.current.set(update.id, update.rank);
		}
		if (isAuthenticated) {
			for (const update of updates) {
				repositoryEntryUpdateMutation({
					id: update.id,
					values: { rank: update.rank },
					repositoryId: currentRepositoryId,
				});
			}
		} else {
			dispatch(entriesUpdateMany(updates));
		}
	}, [dispatch, isAuthenticated, tableEntries, repository?.id, repositoryEntryUpdateMutation]);

	useEffect(() => {
		rankByEntryIdRef.current = new Map(tableEntries.map((entry) => [entry.id, entry.rank]));
	}, [tableEntries]);

	const handleClearVisibleTableFields = useCallback(() => {
		if (!repositoryId) {
			return;
		}
		localStorageSetEntriesTableVisiblePropertyIds(repositoryId, null);
		setVisiblePropertyIds(null);
	}, [repositoryId]);

	const visibleTableFieldCountForChip = useMemo(() => {
		if (isSmScreen) {
			return null;
		}
		if (visiblePropertyIds == null || !repositoryPropertyDefinitions.length) {
			return null;
		}
		const n = visiblePropertyIds.length;
		if (n === 0 || n >= repositoryPropertyDefinitions.length) {
			return null;
		}
		return n;
	}, [isSmScreen, visiblePropertyIds, repositoryPropertyDefinitions.length]);

	if (isNoAccessQueryError(sharedCollectionError)) {
		return <NoAccessView />;
	}
	const isNotFound = isCloudData
		? isNotFoundQueryError(repositoryByIdError) || isNotFoundQueryError(sharedCollectionError)
		: isEntriesHydrated && !repository;
	if (isNotFound) {
		return <NotFoundView />;
	}

	if (!repository || isRepositoryByIdLoading || isRepositoryEntriesLoading) {
		return <Box flex={1} display='flex' justifyContent='center' alignItems='center'><CircularProgress /></Box>;
	}

	return <>
		{repository && <Helmet>
			<title>{repository.title} - {config.appName}</title>
		</Helmet>}
		<RepositoryEntriesFilterArea
			resolvedDataFilter={resolvedDataFilter}
			includeArchived={includeArchived}
			propertyDefinitions={repositoryPropertyDefinitions}
			visibleItemsCount={tableEntries.length}
			visibleTableFieldCount={visibleTableFieldCountForChip}
			onOpenFilters={openFilters}
			onRemoveWhereFilter={handleRemoveWhereFilterChip}
			onIncludeArchivedChange={setIncludeArchived}
			onClearVisibleTableFields={handleClearVisibleTableFields}
			timelineSort={isSmScreen && timelineDateProperties.length ? { dateProperties: timelineDateProperties, onSelect: handleViewAsTimeline } : undefined}
		/>
		<Box sx={{display: 'flex', justifyContent: 'center', flexDirection: 'column', flex: 1, minHeight: 0}} onClick={() => handleEntryBlur()}>
			{isSmScreen ? (<Container
				maxWidth={isSmScreen ? 'sm' : false}
				disableGutters={isSmScreen}
				sx={{minHeight: 0, display: 'flex', flexDirection: 'column', mb: 5, flex: 1}}
			>
				<EntriesList
					rowMenuProps={{
						anchorEl: menuAnchorEl,
						onClose: closeTableRowMenu,
						item: menuEntry,
						anchorOrigin: {vertical: 'top', horizontal: 'right'},
						transformOrigin: {vertical: 'bottom', horizontal: 'right'},
					}}
					onRowMenuGoToClick={handleTableRowMenuGoTo}
					onRowMenuArchiveClick={handleTableRowMenuArchive}
					onRowMenuUnarchiveClick={handleTableRowMenuUnarchive}
					onRowMenuDeleteClick={handleTableRowMenuDelete}
					onReorder={isSharedView ? undefined : handleReorder}
					disableReordering={includeArchived}
					onTouchReorderChange={setIsListReordering}
					isLoading={isRepositoryEntriesLoading}
				>
					{tableEntries.map((entry) => {
						const titleDef = tablePropertyDefinitions[0];
						const label = titleDef
							? entryPropertyGetReadableValue(titleDef, entry.data[titleDef.slug])
							: '';
						return (
							<EntriesListItem
								key={entry.id}
								id={entry.id}
								entry={entry}
								propertyDefinitions={tablePropertyDefinitions}
								label={label} // TODO: deprecate this
								disabled={Boolean(entry.isArchived)}
								isSelected={selectedEntryId === entry.id || focusId === entry.id}
								onClick={(e) => {
									e.stopPropagation();
									handleEntryClick(entry.id);
								}}
								onEntryFocus={() => {
									handleEntryFocus(entry.id);
								}}
							/>
						);
					})}
				</EntriesList>
			</Container>
			) : (<Container
				maxWidth={isSmScreen ? 'sm' : false}
				disableGutters={isSmScreen}
				sx={{height: 'calc(100dvh - 56px)', mt: 0}} // slide the table header a little bit into the page header area 
			>
				<EntriesTable
					propertyDefinitions={tablePropertyDefinitions}
					data={tableEntries}
					disableMetaFieldEdit={isCloudData}
					disableReordering={isSharedView}
					onReorder={isSharedView ? undefined : handleReorder}
					rowMenuProps={{
						onMenuBtnClick: handleRowMenuBtnClick,
						onClose: closeTableRowMenu,
						anchorEl: menuAnchorEl,
						item: menuEntry,
					}}
					onRowMenuArchiveClick={isSharedView ? undefined : handleTableRowMenuArchive}
					onRowMenuUnarchiveClick={isSharedView ? undefined : handleTableRowMenuUnarchive}
					onRowMenuDeleteClick={isSharedView ? undefined : handleTableRowMenuDelete}
					getRowDisabled={(row: RepositoryEntriesRowDto) => Boolean(row.isArchived)}
					onCellEdit={isSharedView ? undefined : handleCellEdit}
					isLoading={isRepositoryEntriesLoading}
					onViewAsTimeline={handleViewAsTimeline}
				/>
			</Container>
			)}
		</Box>
		<AddRepositoryEntryFormDialog
			open={isAddRepositoryEntryDialogOpen}
			repositoryId={addRepositoryEntryDialog?.repositoryId ?? null}
			linkToProperty={addRepositoryEntryDialog?.linkToProperty}
			linkToEntryId={addRepositoryEntryDialog?.linkToEntryId}
			onCreated={handleAddDialogCreated}
			onClose={() => {
				setPendingForeignCreateLink(null);
				closeAddRepositoryEntryDialog();
			}}
			title={repository.singular ? `New ${repository.singular.toLowerCase()}` : ''}
		/>
		<EditRepositoryEntryFieldDialog
			open={Boolean(fieldEditTarget)}
			onClose={closeFieldEditDialog}
			entry={fieldEditTarget?.entry ?? null}
			propertyDefinition={fieldEditTarget?.def ?? null}
			onSubmitValues={handleFieldEditSubmitValues}
			onNavigateToEntry={(entryId) => navigate(toPath(`/entries/${entryId}`))}
			onAddEntryClick={(def, currentEntryIds) => {
				const linkToEntryId = fieldEditTarget?.entry.id;
				const sourceEntryId = fieldEditTarget?.entry.id;
				closeFieldEditDialog();
				if (linkToEntryId && sourceEntryId) {
					setPendingForeignCreateLink({
						entryId: sourceEntryId,
						def,
						currentEntryIds,
					});
					openAddRepositoryEntryDialog({repositoryId: def.targetRepositoryId, linkToProperty: def, linkToEntryId});
				}
			}}
			disabled={Boolean(fieldEditTarget?.entry.isArchived)}
		/>
		<ConfirmDeleteEntryDialog
			open={isConfirmDeleteDialogOpen}
			handleClose={closeConfirmDeleteDialog}
			handleSubmit={() => submitConfirmDeleteDialog({repositoryId: repository.id})}
			entry={confirmDeleteDialogEntry}
			title={confirmDeleteDialogTitle}
			dialogContent={confirmDeleteEntryLabel}
		/>
		<EntriesFilterDialog
			open={isEntriesFilterDialogOpen}
			initialWhere={whereFilter}
			initialIncludeArchived={includeArchived}
			properties={chartFilterPropertyOptions}
			{...(isSmScreen
				? {}
				: {
					fieldOptions: entriesTableFieldOptions,
					initialVisiblePropertyIds: visiblePropertyIds,
				})}
			onClose={closeFilters}
			onApply={({ where, includeArchived: nextIncludeArchived, visiblePropertyIds: nextVisible }) => {
				applyWhereAndArchived({ where, includeArchived: nextIncludeArchived });
				if (nextVisible !== undefined) {
					localStorageSetEntriesTableVisiblePropertyIds(repository.id, nextVisible);
					setVisiblePropertyIds(nextVisible);
				}
				closeFilters();
			}}
		/>
		{!isListReordering && <FabAreaWrapperBottomRight>
			<Box display='flex' flexDirection='column' justifyContent='center' alignItems='center' gap={2}>
				{selectedListEntry && !isSharedView && <ActionAreaBtn aria-label='Entry actions' onClick={(e) => handleRowMenuBtnClick(selectedListEntry, e.currentTarget)}>
					<MoreVertIcon />
				</ActionAreaBtn>}
				{selectedListEntry && <ActionAreaFab size='large' aria-label='Go to entry' onClick={() => navigate(toPath(`/entries/${selectedListEntry.id}`))}>
					<ArrowForwardIcon />
				</ActionAreaFab>}
				{!isSharedView && !selectedListEntry && <ActionAreaBtn aria-label='More collection actions' onClick={(e) => setCollectionMenuAnchorEl(e.currentTarget)}>
					<MoreVertIcon />
				</ActionAreaBtn>}
				<Menu
					anchorEl={collectionMenuAnchorEl}
					open={Boolean(collectionMenuAnchorEl)}
					onClose={() => setCollectionMenuAnchorEl(null)}
					anchorOrigin={{vertical: 'top', horizontal: 'right'}}
					transformOrigin={{vertical: 'bottom', horizontal: 'right'}}
				>
					<MenuItem
						component={Link}
						to={`/collection/${repository.id}/settings`}
						onClick={() => setCollectionMenuAnchorEl(null)}
					>
						<ListItemIcon><SettingsIcon fontSize='small' /></ListItemIcon>
						<ListItemText>Configure collection</ListItemText>
					</MenuItem>
					<MenuItem
						onClick={() => {
							setCollectionMenuAnchorEl(null);
							setIsShareDialogOpen(true);
						}}
					>
						<ListItemIcon><ShareIcon fontSize='small' /></ListItemIcon>
						<ListItemText>Share</ListItemText>
					</MenuItem>
				</Menu>
				{!isSharedView && !selectedListEntry && <ActionAreaFab size='large' onClick={() => openAddRepositoryEntryDialog({repositoryId: repository.id})}>
					<AddIcon />
				</ActionAreaFab>}
			</Box>
		</FabAreaWrapperBottomRight>}
		{isListReordering && <FabAreaWrapperBottomCenter>
			<Typography variant='body2' sx={{px: 2, py: 1, borderRadius: 999, bgcolor: 'divider', backdropFilter: 'blur(8px)', whiteSpace: 'nowrap'}}>
				Tap on another entry to reorder
			</Typography>
		</FabAreaWrapperBottomCenter>}
		{!isListReordering && selectedListEntry && <FabAreaWrapperBottomCenter>
			<ActionAreaBtn aria-label='Unselect entry' onClick={handleEntryBlur} sx={{color: 'text.primary'}}>
				<CloseIcon />
			</ActionAreaBtn>
		</FabAreaWrapperBottomCenter>}
		{!isSharedView && <ShareRepositoryDialog
			open={isShareDialogOpen}
			onClose={() => setIsShareDialogOpen(false)}
			repositoryId={repository.id}
			repositoryTitle={repository.title}
			type={RepositoryAccessKeyType.Read}
		/>}
	</>;
};

export default RepositoryEntriesView;